import Vue from 'vue'
import VueMeta from 'vue-meta'
import VueRouter from 'vue-router'
import { START_LOCATION } from 'vue-router'

import routes from './routes'
import { Auth } from '@/mixin/auth'
import store from '@/state/store'
import { MixPanel } from '@/mixin/mixpanel'
import { Helper } from '@/mixin/helper'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  base: process.env.NODE_ENV === 'production' ? '/' : '/',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  try {
    const savedState = localStorage.getItem('vue-update-state')
    if (savedState) {
      const state = JSON.parse(savedState)
      if (state.updateDetected && routeTo.path !== routeFrom.path) {
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration && registration.waiting) {
          // Clear the state only if we're actually activating
          localStorage.removeItem('vue-update-state')

          registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.href = routeTo.fullPath
          })
          return
        } else {
          localStorage.removeItem('vue-update-state')
        }
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error checking SW update state:', error)
  }

  if (
    routeFrom === START_LOCATION &&
    store.state.identity.whoami != null &&
    routeTo.name != 'Live Track Fleet Task Instant' &&
    routeTo.name != 'Tracking Temporary Liveview' &&
    routeTo.name != 'Track FO' &&
    routeTo.name != 'Track DO' &&
    routeTo.name != 'Route Tracking'
  ) {
    await Auth.methods.getAuth()

    if (store.getters['identity/hasAccess'](['vgroups:read'])) {
      await Auth.methods.getVgroups()
    }
  }

  if (process.env.VUE_APP_DEFAULT_AUTH === 'authapi') {
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired)

    if (!authRequired) {
      return next()
    }

    // Checking for localstorage
    const token = store.state.identity.whoami
    if (!token) {
      if (routeTo.name === 'Route Tracking') {
        const data = {
          hasButton: false,
        }
        localStorage.setItem('404', JSON.stringify(data))
        window.location.href = '/404'
        return
      }
      window.location.href = '/login'
    }

    const guestPage = routeTo.matched.some((route) => route.meta.guestPage)
    const hasDashboardAccess = process.env.VUE_APP_HAS_DASHBOARD_ACCESS || true

    if (hasDashboardAccess === 'false' && !guestPage) {
      localStorage.removeItem('token')

      window.location.href = '/login'
    }

    const hasAccess = routeTo.matched.some((route) => route.meta.hasAccess)
    if (!hasAccess) {
      window.location.href = '/'
    }

    next()
  } else {
    // ini hanya sementara, lewatkan semua pengecekan menu yang required
    // saat ini masih manual disetiap route menu di route.js
    next()
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

router.afterEach((to, from) => {
  // Scroll to top when navigating to a new page
  let currentUrl = new URL(window.location.href)

  const searchParams = new URLSearchParams(currentUrl.search)
  const action = searchParams.get('action')
  const meta = searchParams.get('meta')

  if (action === 'track' && meta) {
    const params = JSON.parse(Helper.methods.decrypt(meta))
    if (to.name !== '404') {
      MixPanel.methods.track('Redirect Link', {
        Source: params.source,
        Domain: params.company.domain,
        'Company Name': params.company.name,
        Id: params.id,
        Page: to.name,
      })
    }

    const cleanUrl =
      window.location.protocol + '//' + window.location.host + window.location.pathname
    history.pushState({}, document.title, cleanUrl)
  }
})

export default router
